import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Typography, Stack, Box, Grid, Paper, TextField, MenuItem, FormControl, InputLabel, Select, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, IconButton, Menu, Drawer, useMediaQuery, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, ListItemIcon, ListItemText, Divider, Card, CardContent } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { Edit, TrendingUp, Assignment, Timeline, Folder, Add as AddIcon, Search as SearchIcon, Clear as ClearIcon, PostAdd, DeleteOutline, MoreVert, Visibility, LibraryBooks, TaskAlt, RestartAlt, AddTask, AlarmOff } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { deleteData, getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import CreateEpicPage from './createEpic';
import EpicDetailsPage from './epicDetailsPage';
import Loader from '../../../common/spinner';
import CreateUserStory from './userStoryManagement/createStory';
import CreateTask from './taskManagement/createTask';
import { formatDate } from '../../../utils/dateFormat';
import { getStatusColor, priorityColors } from '../../../utils/statusColor';
import StatsCard from '../../../components/common/StatsCard';

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const ChipBox = ({ label, styles }) => (
    <Box sx={{
        minWidth: '70px', textAlign: 'center', px: 1.5, py: 0.5, borderRadius: '20px',
        display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, textTransform: 'capitalize', ...styles
    }}>
        {label}
    </Box>
);

function EpicDashboard() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
    const hasFetchedProjects = useRef(false);
    const queryParams = new URLSearchParams(location.search);
    const urlProjectTitle = queryParams.get('projectTitle');

    const [state, setState] = useState({
        isMyView: false, projects: [], epics: [], loadingEpics: false, loadingProjects: true,
        search: '', localSearch: '', project: urlProjectTitle || 'all', status: 'all', priority: 'all', timeline: 'all',
        page: 0, rowsPerPage: 10, totalCount: 0, anchorEl: null, selectedEpic: null,
        isModalOpen: false, isDeleteModalOpen: false, isDetailsModalOpen: false, deleting: false,
        isEditing: false,
        isStoryModalOpen: false,
        isTaskModalOpen: false,
    });

    const updateState = useCallback((updates) => {
        setState(prev => ({ ...prev, ...updates }));
    }, []);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedEpic, setSelectedEpic] = useState(null);
    const detailsPageRef = useRef();

    useEffect(() => {
        if (urlProjectTitle) {
            updateState({ project: urlProjectTitle, page: 0 });
        }
    }, [urlProjectTitle, updateState]);

    const fetchProjects = useCallback(async () => {
        try {
            const res = await getData(API_ENDPOINTS.fetchProjectsBySupervisor(userId));
            updateState({ projects: res.status === "SUCCESS" ? res.message.data : [], loadingProjects: false });
        } catch { updateState({ projects: [], loadingProjects: false }); }
    }, [userId, updateState]);

    const fetchEpics = useCallback(async () => {
        updateState({ loadingEpics: true });
        const timerPromise = wait(500);

        try {
            let start_date = '', end_date = '';
            if (state.timeline !== 'all') {
                end_date = new Date().toISOString().split('T')[0];
                if (state.timeline === 'today') start_date = end_date;
                else if (state.timeline === 'lastWeek') {
                    const d = new Date(); d.setDate(d.getDate() - 7); start_date = d.toISOString().split('T')[0];
                } else if (state.timeline === 'thisMonth') {
                    start_date = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
                }
            }

            const filters = {
                title: state.search,
                project_name: state.project === 'all' ? '' : state.project,
                status: state.status === 'all' ? '' : state.status,
                priority: state.priority === 'all' ? '' : state.priority,
                isMyView: state.isMyView,
                page: state.page + 1,
                limit: state.rowsPerPage,
                start_date,
                end_date
            };

            userRole === 'Manager' ? filters.manager_id = userId : filters.team_lead_id = userId;

            const res = await getData(API_ENDPOINTS.getAllEpics, filters);
            await timerPromise;

            if (res.status === "SUCCESS") {
                updateState({
                    epics: res?.message?.epics || [],
                    totalCount: res?.message?.pagination?.total || 0,
                    stats: res?.message?.stats // No need to reference state.stats here anymore
                });
            } else {
                updateState({ epics: [] });
            }
        } catch (error) {
            await timerPromise;
            updateState({ epics: [] }); // stats will naturally stay as they are in the 'prev' state
        } finally {
            updateState({ loadingEpics: false });
        }
        // Remove state.stats from this dependency array:
    }, [userId, state.search, state.project, state.status, state.priority, state.timeline, state.isMyView, state.page, state.rowsPerPage, updateState]);

    useEffect(() => {
        if (userId && !hasFetchedProjects.current) {
            fetchProjects();
            hasFetchedProjects.current = true;
        }
    }, [userId, fetchProjects]);

    // Effect for Epics - Runs when filters change
    useEffect(() => {
        fetchEpics();
    }, [fetchEpics]);

    // Debounced search effect
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (state.localSearch.length >= 3 || state.localSearch.length === 0) {
                updateState({ search: state.localSearch, page: 0 });
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [state.localSearch, updateState]);

    const confirmDelete = async () => {
        updateState({ deleting: true });
        try {
            const res = await deleteData(`${API_ENDPOINTS.deleteEpic}/${state.selectedEpic.id}`);
            if (res?.status === "SUCCESS") fetchEpics();
        } finally { updateState({ deleting: false, isDeleteModalOpen: false, selectedEpic: null }); }
    };

    const hasNoEpics = !state.epics || state.epics.length === 0;

    const handleOpenMenu = (event, epic) => {
        setAnchorEl(event.currentTarget);
        setSelectedEpic(epic);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedEpic(null);
    };

    const handleReset = () => {
        updateState({
            localSearch: '',
            search: '',
            project: 'all',
            status: 'all',
            priority: 'all',
            timeline: 'all',
            page: 0
        });
    };

    const dashboardStats = [
        { title: 'All Epics', value: state.stats?.total_epics, icon: <TrendingUp />, color: '#6366F1' },
        { title: 'Todo', value: state.stats?.total_todo, icon: <Assignment />, color: '#F59E0B' },
        { title: 'In Progress', value: state.stats?.total_in_progress, icon: <Timeline />, color: '#3B82F6' },
        { title: 'Completed', value: state.stats?.total_completed, icon: <Folder />, color: '#10B981' },
        { title: 'Overdue', value: state.stats?.total_overdue, icon: <AlarmOff />, color: '#EF4444' },
    ];

    const EpicMobileCard = ({ epic }) => (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography sx={{ fontWeight: 700, color: '#1E293B', wordBreak: 'break-word' }}>
                        {epic.title}
                    </Typography>
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, epic)} sx={{ flexShrink: 0 }}>
                        <MoreVert fontSize="small" />
                    </IconButton>
                </Stack>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }} color="text.secondary">
                    {epic.project_title || 'N/A'}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent={'space-between'} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction={'row'} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <ChipBox
                            label={epic.status}
                            styles={{
                                bgcolor: getStatusColor(epic.status).bg,
                                color: getStatusColor(epic.status).color,
                                border: `1px solid ${getStatusColor(epic.status).border}`
                            }}
                        />
                        <ChipBox
                            label={epic.priority}
                            styles={{
                                bgcolor: priorityColors[epic.priority]?.bg || '#f1f5f9',
                                color: priorityColors[epic.priority]?.color || '#64748b',
                                border: `1px solid ${priorityColors[epic.priority]?.border || '#e2e8f0'}`
                            }}
                        />
                    </Stack>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                        {`${Math.round(epic.progress || 0)}% complete`}
                    </Typography>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {(!epic.start_date && !epic.end_date) ? (
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: '600' }}>
                        Timeline not available
                    </Typography>
                ) : !epic.end_date ? (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatDate(epic.start_date)}</Typography>
                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: '500', fontSize: '0.7rem' }}>End date pending</Typography>
                    </Stack>
                ) : (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatDate(epic.start_date)}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>to {formatDate(epic.end_date)}</Typography>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" mt={{ xs: 1.5, sm: 0 }} mb={3} alignItems="center" flexWrap="wrap" gap={2}>
                <Typography variant="h5" fontWeight="700">Epics Overview</Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => updateState({ isModalOpen: true })}
                        sx={{
                            borderRadius: '8px',
                            bgcolor: '#6366f1',
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 3 }
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Epic</Box>
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={3} mb={4}>
                {dashboardStats.map((card, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={idx}>
                        <StatsCard
                            title={card.title}
                            value={card.value || 0}
                            icon={card.icon}
                            color={card.color}
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2} mb={2}>
                <Grid size={{ xs: 12, md: 3, lg: 4 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by epic name..."
                        value={state.localSearch}
                        onChange={(e) => updateState({ localSearch: e.target.value })}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} />,
                            endAdornment: state.localSearch && (
                                <IconButton size="small" onClick={() => updateState({ localSearch: '', search: '', page: 0 })}>
                                    <ClearIcon fontSize="inherit" />
                                </IconButton>
                            )
                        }}
                    />
                </Grid>
                {['project', 'status', 'priority'].map((key) => (
                    <Grid size={{ xs: 12, sm: 6, md: 2.3 }} key={key}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ textTransform: 'capitalize' }}>{key}</InputLabel>
                            <Select
                                label={key}
                                value={state[key]}
                                onChange={(e) => updateState({ [key]: e.target.value, page: 0 })}
                            >
                                <MenuItem value="all">All</MenuItem>
                                {key === 'project' ? (
                                    [
                                        (state.project !== 'all' && !state.projects.find(p => p.project_title === state.project)) && (
                                            <MenuItem key="url-default" value={state.project}>{state.project}</MenuItem>
                                        ),
                                        ...state.projects.map(p => (
                                            <MenuItem key={p.id} value={p.project_title}>{p.project_title}</MenuItem>
                                        ))
                                    ].filter(Boolean) // Remove nulls from the array
                                ) : key === 'status' ? (
                                    ['Todo', 'In Progress', 'Completed', 'On Hold', 'Overdue'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)
                                ) : (
                                    ['High', 'Medium', 'Low'].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 6, md: 2.1, lg: 1.1 }}>
                    <Button fullWidth startIcon={<RestartAlt />} onClick={handleReset} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
            </Grid>

            {state.loadingEpics ? (
                <Paper sx={{ py: 10, borderRadius: '12px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </Paper>
            ) : hasNoEpics ? (
                <Box textAlign="center" py={10}>
                    <Typography variant="h6" color="textSecondary" fontWeight="600">No Epics Available</Typography>
                    <Typography variant="body2" color="textSecondary">We couldn't find any epics. Try adjusting your filters or create a new one.</Typography>
                </Box>
            ) : (
                <>
                    <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                        {state.epics.map((epic) => (
                            <EpicMobileCard key={epic.id} epic={epic} />
                        ))}
                    </Stack>

                    <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                                    <TableRow>
                                        {['Epic Name', 'Project', 'Status', 'Priority', 'Progress', 'Timeline', 'Actions'].map(h =>
                                            <TableCell key={h} sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>{h}</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                                    {state.epics.map((epic) => (
                                        <TableRow key={epic.id} hover sx={{
                                            '&:hover': {
                                                bgcolor: '#F1F5F9 !important'
                                            }
                                        }}>
                                            <TableCell sx={{ fontWeight: 500, color: '#1E293B', width: '300px' }}>{epic.title}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>{epic.project_title || 'N/A'}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <ChipBox
                                                    label={epic.status}
                                                    styles={{
                                                        bgcolor: getStatusColor(epic.status).bg,
                                                        color: getStatusColor(epic.status).color,
                                                        border: `1px solid ${getStatusColor(epic.status).border}`
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <ChipBox
                                                    label={epic.priority}
                                                    styles={{
                                                        bgcolor: priorityColors[epic.priority]?.bg || '#f1f5f9',
                                                        color: priorityColors[epic.priority]?.color || '#64748b',
                                                        border: `1px solid ${priorityColors[epic.priority]?.border || '#e2e8f0'}`
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                                    {/* Background Circle */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={100}
                                                        size={42}
                                                        thickness={4}
                                                        sx={{ color: '#e2e8f0' }}
                                                    />
                                                    {/* Active Progress Circle */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={Math.min(epic.progress || 0, 100)}
                                                        size={42}
                                                        thickness={4}
                                                        sx={{
                                                            color: epic.progress >= 100 ? '#22c55e' : '#6366f1',
                                                            position: 'absolute',
                                                            left: 0,
                                                            [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                                                        }}
                                                    />
                                                    {/* Percentage Text Overlay */}
                                                    <Box
                                                        sx={{
                                                            top: 0, left: 0, bottom: 0, right: 0,
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                                            {`${Math.round(epic.progress || 0)}%`}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                {(!epic.start_date && !epic.end_date) ? (
                                                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: '600' }}>
                                                        Timeline not available
                                                    </Typography>
                                                ) : !epic.end_date ? (
                                                    <Stack>
                                                        <Typography variant="caption" display="block" fontWeight="500">
                                                            {formatDate(epic.start_date)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: '500' }}>
                                                            End date pending
                                                        </Typography>
                                                    </Stack>
                                                ) : (
                                                    <>
                                                        <Typography variant="caption" display="block" fontWeight="500">
                                                            {formatDate(epic.start_date)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            to {formatDate(epic.end_date)}
                                                        </Typography>
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <IconButton size="small" onClick={(e) => handleOpenMenu(e, epic)}>
                                                    <MoreVert fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </TableContainer>
                    <TablePagination component="div" count={state.totalCount} page={state.page} rowsPerPage={state.rowsPerPage}
                        onPageChange={(_, p) => updateState({ page: p })} onRowsPerPageChange={(e) => updateState({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })} />
                </>
            )}

            {/* Menu and Dialogs */}
            {(() => {
                const menuItemsContent = (
                    <>
                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const id = selectedEpic.id;
                            navigate(`${roleBasePath}/epic-management/${id}`);
                        }}>
                            <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>View Epic</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { updateState({ isStoryModalOpen: true, selectedEpic: selectedEpic }); handleCloseMenu(); }}>
                            <ListItemIcon><PostAdd fontSize="small" sx={{ color: '#6366f1' }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Create User Story</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => { updateState({ isTaskModalOpen: true, selectedEpic: selectedEpic }); handleCloseMenu(); }}>
                            <ListItemIcon><PostAdd fontSize="small" sx={{ color: '#10b981' }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Create Task</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const pId = selectedEpic?.project_id;
                            const pName = selectedEpic?.project_title;
                            const eId = selectedEpic?.id;
                            navigate(`${roleBasePath}/user-stories?projectId=${pId}&projectTitle=${pName}&epicId=${eId}`);
                        }}>
                            <ListItemIcon><LibraryBooks fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>View Stories</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            handleCloseMenu();
                            const pId = selectedEpic?.project_id;
                            const pName = selectedEpic?.project_title;
                            const eId = selectedEpic?.id;
                            navigate(`${roleBasePath}/user-tasks?projectId=${pId}&projectTitle=${pName}&epicId=${eId}&origin=Epic`);
                        }}>
                            <ListItemIcon><TaskAlt fontSize="small" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>View Tasks</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { handleCloseMenu(); updateState({ isDeleteModalOpen: true, selectedEpic: selectedEpic }); }} sx={{ color: 'error.main' }}>
                            <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Delete Epic</ListItemText>
                        </MenuItem>
                    </>
                );

                return isMobile ? (
                    <Drawer
                        anchor="bottom"
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        PaperProps={{ sx: { borderTopLeftRadius: '16px', borderTopRightRadius: '16px', pb: 1 } }}
                    >
                        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', mx: 'auto', mt: 1.5, mb: 0.5 }} />
                        {menuItemsContent}
                    </Drawer>
                ) : (
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleCloseMenu}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{ sx: { width: 220, borderRadius: '12px', mt: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
                    >
                        {menuItemsContent}
                    </Menu>
                );
            })()}

            {/* Modal Dialogs */}
            <Dialog open={state.isDeleteModalOpen}
                onClose={(event, reason) => {
                    // Prevent closing if the user clicks outside
                    if (reason === 'backdropClick') return;
                    if (!state.deleting) updateState({ isDeleteModalOpen: false });
                }}
                PaperProps={{ sx: { borderRadius: { xs: 0, sm: '12px' }, m: { xs: 0, sm: 4 }, width: { xs: '100%', sm: 'auto' }, p: 1 } }}>
                <DialogTitle>Delete Epic</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete <strong>{state.selectedEpic?.title}</strong>? This action cannot be undone.</DialogContentText></DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => updateState({ isDeleteModalOpen: false })} disabled={state.deleting} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" disabled={state.deleting} sx={{ textTransform: 'none' }}>{state.deleting ? 'Deleting...' : 'Delete Epic'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={state.isModalOpen}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') return;
                    updateState({ isModalOpen: false });
                }}
                fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: { xs: 0, sm: '12px' }, m: { xs: 0, sm: 4 }, width: { xs: '100%', sm: 'auto' }, maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }, p: 1 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Create New Epic
                    <IconButton onClick={() => updateState({ isModalOpen: false })} size="small"><ClearIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <CreateEpicPage projects={state.projects} onCancel={() => updateState({ isModalOpen: false })} onSuccess={() => { fetchEpics(); updateState({ isModalOpen: false }); }} />
                </DialogContent>
            </Dialog>

            {/* <Dialog open={state.isDetailsModalOpen} onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                updateState({ isDetailsModalOpen: false, selectedEpic: null, isEditing: false });
            }} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '12px', p: 1 } }} >
                <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{ bgcolor: '#6366f1', color: 'white', p: 1.5, borderRadius: '10px', display: 'flex', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)' }}>
                                <Assignment fontSize="medium" />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="700" sx={{ color: '#1e293b', lineHeight: 1.2 }}>{state.selectedEpic?.title}</Typography>
                                <Box sx={{ fontSize: '12px' }}>Epic ID: {state.selectedEpic?.id}</Box>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {state.isEditing ? (
                                <>
                                    <Button variant="contained" size="small" color="success" sx={{ textTransform: 'none', fontWeight: 600 }} onClick={() => detailsPageRef.current?.handleSave()}>Save Changes</Button>
                                    <Button variant="text" size="small" color="error" onClick={() => updateState({ isEditing: false })} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                                </>
                            ) : (
                                <Button variant="outlined" size="small" startIcon={<Edit sx={{ fontSize: 18 }} />} onClick={() => updateState({ isEditing: true })} sx={{ textTransform: 'none', borderRadius: '6px', fontWeight: 600 }}>Edit</Button>
                            )}
                            <IconButton onClick={() => updateState({ isDetailsModalOpen: false, selectedEpic: null, isEditing: false })} size="small"><ClearIcon fontSize="small" /></IconButton>
                        </Stack>
                    </Stack>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {state.selectedEpic ? (
                        <EpicDetailsPage ref={detailsPageRef} epicId={state.selectedEpic.id} epicData={state.selectedEpic} isEditing={state.isEditing} onEditSuccess={() => { fetchEpics(); updateState({ isEditing: false }); }} onClose={() => updateState({ isDetailsModalOpen: false, selectedEpic: null, isEditing: false })} />
                    ) : (
                        <Box p={10} display="flex" justifyContent="center" alignItems="center"><Loader /></Box>
                    )}
                </DialogContent>
            </Dialog> */}

            <Dialog open={state.isStoryModalOpen} onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                updateState({ isStoryModalOpen: false, selectedEpic: null });
            }} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: { xs: 0, sm: '12px' }, m: { xs: 0, sm: 4 }, width: { xs: '100%', sm: 'auto' }, maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }, p: 1 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex' }}>
                        <Box sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6', p: 1.2, borderRadius: '10px', display: 'flex', mr: 2 }}><Assignment sx={{ fontSize: 22 }} /></Box>
                        <Box>
                            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>Create User Story</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block' }}>
                                Epic: <Box component="span" sx={{ color: '#475569', fontWeight: 700 }}>{state.selectedEpic?.title}</Box> {state.selectedEpic?.id && ` (ID: ${state.selectedEpic.id})`}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => updateState({ isStoryModalOpen: false })} size="small"><ClearIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <CreateUserStory epicData={state.selectedEpic} onCancel={() => updateState({ isStoryModalOpen: false, selectedEpic: null })} onSuccess={() => { fetchEpics(); updateState({ isStoryModalOpen: false, selectedEpic: null }); }} />
                </DialogContent>
            </Dialog>

            <Dialog open={state.isTaskModalOpen}
                onClose={(event, reason) => {
                    if (reason === 'backdropClick') return;
                    updateState({ isTaskModalOpen: false })
                }} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: { xs: 0, sm: '12px' }, m: { xs: 0, sm: 4 }, width: { xs: '100%', sm: 'auto' }, maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }, p: 1 } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex' }}>
                        <Box sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6', p: 1.2, borderRadius: '10px', display: 'flex', mr: 2 }}><AddTask sx={{ fontSize: 22 }} /></Box>
                        <Box>
                            <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>Create Task</Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block' }}>
                                Epic: <Box component="span" sx={{ color: '#475569', fontWeight: 700 }}>{state.selectedEpic?.title}</Box> {state.selectedEpic?.id && ` (ID: ${state.selectedEpic.id})`}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={() => updateState({ isTaskModalOpen: false })} size="small"><ClearIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <CreateTask epicData={state.selectedEpic} onCancel={() => updateState({ isTaskModalOpen: false, selectedEpic: null })} onSuccess={() => { fetchEpics(); updateState({ isTaskModalOpen: false, selectedEpic: null }); }} />
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default EpicDashboard;